import { makeStyles, CircularProgress } from "@material-ui/core"
import {
  ClientFragment,
  Enum_Safety_Plan_Status_Enum,
  useGetPlanByUserIdAndTemplateNameQuery,
} from "../../../generated/graphql"
import { useGraphQLClient } from "../../../hooks/useGraphQLClient"
import useOrganizationFeatureFlags from "../../../hooks/useOrganizationFeatureFlags"
import SafetyPlanInProgress from "../../molecules/safetyPlan/SafetyPlanInProgress"
import SafetyPlan from "./SafetyPlan"
import SafetyPlanPractitioner from "./SafetyPlanPractitioner"
import {
  BRITE_SAFETY_PLAN,
  RELAPSE_PREVENTION_PLAN,
} from "../../../utils/constants"

/**
 * Entry point for the practitioner's safety plan tab for a given client.
 * Fetches the client's safety plan status and renders one of three views:
 * - `SafetyPlan` — if the client has a completed safety plan.
 * - `SafetyPlanPractitioner` — if no plan exists but MVP2 features are enabled
 *   for the organisation (allows the practitioner to initiate one).
 * - `SafetyPlanInProgress` — fallback when no plan exists and MVP2 is disabled.
 *
 * @param client - The client whose safety plan overview is being displayed.
 */
export default function PlanOverview({
  client,
  planName,
}: {
  client: ClientFragment
  planName: string
}) {
  const classes = useStyles()
  const graphqlClient = useGraphQLClient()

  const { showMVP2Features } = useOrganizationFeatureFlags()
  const showSectionDescription = planName !== RELAPSE_PREVENTION_PLAN

  const { data: planData, isLoading } = useGetPlanByUserIdAndTemplateNameQuery(
    graphqlClient,
    {
      userId: client.id,
      excludedStatus: Enum_Safety_Plan_Status_Enum.Rejected, // Do not retrieve rejected plans
      templateName: planName,
    }
  )

  const hasPlan =
    (planData?.safety_plan_template?.[0]?.safety_plans?.length ?? 0) > 0
  const planType = planData?.safety_plan_template[0]?.plan_type
  const clientName = `${client.first_name} ${client.last_name}`

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }

  if (hasPlan)
    return (
      <SafetyPlan
        client={client}
        planData={planData}
        showSectionDescription={showSectionDescription}
      />
    )
  // Only BRITE plan users w/ mvp2 feature status get the "Create new plan" view
  if (showMVP2Features && planName === BRITE_SAFETY_PLAN)
    return <SafetyPlanPractitioner client={client} planData={planData} />
  return (
    <div className={classes.planContainer}>
      <SafetyPlanInProgress name={clientName} planType={planType} />
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  planContainer: {
    display: "flex",
    width: "1400px",
    padding: "44px 32px",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
  },
  loadingContainer: {
    minHeight: 120,
    height: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
  },
}))
